import { Plus, Trash2, AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { Button } from '@centraoneplus/ui';
import type { EscalationAction, EscalationRule, EscalationOrgTarget } from '@services/sla.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

/**
 * Editor for a policy's SLA escalation ladder.
 *
 * Until this existed, `escalationRules` could only be authored over the API, so
 * every policy created through Settings saved an empty ladder and no escalation
 * ever fired — the reason a breached ticket notified only its assignee.
 *
 * Deliberately covers the `notify` action only: naming WHO is told at each level
 * is the whole point of a ladder, while `assign` / `priority_change` / `webhook`
 * remain API-only. Rules carrying those actions are preserved untouched when
 * edited here (see `patchNotifyConfig`), so an API-authored policy is never
 * silently downgraded by someone opening this form.
 */

const TARGET_TYPES: Array<{ value: EscalationRule['targetType']; label: string }> = [
  { value: 'first_response', label: 'First response' },
  { value: 'next_response', label: 'Next response' },
  { value: 'resolution', label: 'Resolution' },
];

/**
 * `offsetLabel` must never be empty: it labels the third column, and a blank
 * label renders as dead space that leaves the input visually unaligned with the
 * two selects beside it. `at_breach` needs no offset, so that column is hidden
 * entirely rather than labelled with nothing.
 */
const TRIGGERS: Array<{
  value: EscalationRule['trigger'];
  label: string;
  offsetLabel: string;
  unit: string;
}> = [
  { value: 'before_target', label: 'Before the deadline', offsetLabel: 'How early', unit: 'min' },
  { value: 'at_breach', label: 'When it breaches', offsetLabel: '', unit: '' },
  { value: 'after_breach', label: 'After it breaches', offsetLabel: 'How late', unit: 'min' },
  { value: 'percent_of_target', label: 'At % of the SLA budget', offsetLabel: 'Budget used', unit: '%' },
];

/** The current trigger, tolerating a rule whose trigger was never stored. */
function triggerOf(rule: EscalationRule) {
  return TRIGGERS.find((t) => t.value === rule.trigger) ?? TRIGGERS[1];
}

/**
 * Labels mirror how notification-service resolves each rung, so the form does
 * not promise an org structure the backend cannot find.
 */
const ORG_TARGETS: Array<{ value: EscalationOrgTarget; label: string; help: string }> = [
  { value: 'assignee', label: 'Assignee', help: "The ticket's current assignee" },
  { value: 'team_lead', label: 'Team Lead', help: "The ticket team's supervisor" },
  { value: 'manager', label: 'Manager', help: 'Tenant admins' },
  { value: 'department_head', label: 'Department Head', help: 'Department-head role, else the owner' },
];

/**
 * Concrete tenant roles, offered alongside the org rungs.
 *
 * The ladder rungs are RELATIVE to the ticket (this team's lead, the tenant's
 * admins); these are ABSOLUTE — "everyone holding this role", written to
 * `config.roles[]`. Both can be set on one level and the backend de-duplicates
 * the resulting recipients, so a person who is both is paged once.
 *
 * Mirrors `UserRole` in @centraoneplus/shared, lowest to highest. `light_agent`
 * is deliberately omitted: paging the most restricted role about a breached SLA
 * is not a meaningful escalation.
 */
const ROLE_TARGETS: Array<{ value: string; label: string; help: string }> = [
  { value: 'agent', label: 'Agent', help: 'Every active agent in the tenant' },
  { value: 'supervisor', label: 'Supervisor', help: 'Every active supervisor in the tenant' },
  { value: 'admin', label: 'Admin', help: 'Every active admin in the tenant' },
  { value: 'owner', label: 'Owner', help: 'The tenant owner' },
];

interface Props {
  value: EscalationRule[];
  onChange: (rules: EscalationRule[]) => void;
}

/** The notify action a row edits — the first notify/email action on the rule. */
function notifyAction(rule: EscalationRule) {
  return rule.actions?.find((a) => a.type === 'notify' || a.type === 'email');
}

function selectedTargets(rule: EscalationRule): EscalationOrgTarget[] {
  return (notifyAction(rule)?.config?.targets as EscalationOrgTarget[] | undefined) ?? [];
}

function selectedRoles(rule: EscalationRule): string[] {
  return (notifyAction(rule)?.config?.roles as string[] | undefined) ?? [];
}

function selectedChannels(rule: EscalationRule): string[] {
  // An empty/absent channel list means "both", which is the backend default.
  return notifyAction(rule)?.config?.channels ?? ['in_app', 'email'];
}

/**
 * Patch only the notify action's config, leaving every other action on the rule
 * (assign / priority_change / webhook, authored over the API) exactly as it was.
 */
function patchNotifyConfig(
  rule: EscalationRule,
  patch: Record<string, unknown>,
): EscalationRule {
  const actions = rule.actions?.length ? [...rule.actions] : [{ type: 'notify' as const, config: {} }];
  const index = actions.findIndex((a) => a.type === 'notify' || a.type === 'email');
  const at = index >= 0 ? index : actions.push({ type: 'notify', config: {} }) - 1;
  actions[at] = { ...actions[at], config: { ...actions[at].config, ...patch } };
  return { ...rule, actions };
}

/**
 * A stable identity for the rule, minted once here.
 *
 * The backend keys "has this level already escalated this ticket?" on
 * (ticketId, ruleId), and derives ruleId from the rule's SHAPE when none is
 * stored. Without an id, editing a level's threshold changes its identity, which
 * re-arms it for every open ticket and re-pages everyone. Tuning the threshold
 * is the routine admin action on an approaching-breach level, so the id is not
 * optional in practice.
 */
function newRuleId(): string {
  return globalThis.crypto && 'randomUUID' in globalThis.crypto
    ? globalThis.crypto.randomUUID()
    : `esc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * A ladder that starts BEFORE the deadline.
 *
 * The previous default put level 1 at the breach, so a policy created through
 * Settings could only ever react after the SLA was already missed. Level 1 now
 * gives the team lead a 30-minute head start (the tenant's own warning threshold
 * takes over if this is cleared to 0), level 2 fires at the breach, and each
 * level above that adds an hour.
 */
function newRule(level: number): EscalationRule {
  return {
    id: newRuleId(),
    level,
    name: `Level ${level}`,
    isActive: true,
    targetType: 'resolution',
    trigger: level === 1 ? 'before_target' : level === 2 ? 'at_breach' : 'after_breach',
    ...(level === 1
      ? { delayMinutes: 30 }
      : level === 2
        ? { delayMinutes: 0 }
        : { delayMinutes: (level - 2) * 60 }),
    actions: [{ type: 'notify', config: { targets: ['team_lead'], channels: ['in_app', 'email'] } }],
  };
}

/**
 * The policy's approaching-breach thresholds, needed to reproduce the id of a
 * zero-lead `before_target` level (it inherits this lead — see below).
 */
export interface RuleIdContext {
  firstResponseWarning?: number | null;
  resolutionWarning?: number | null;
}

/** Mirror of `DEFAULT_WARNING_LEAD_MINUTES` in @centraoneplus/shared. */
const DEFAULT_WARNING_LEAD_MINUTES = 15;

/**
 * Re-derive the id the backend keys the "already escalated" dedup row on, for a
 * level that shipped without one.
 *
 * This is a DELIBERATE, load-bearing copy of `deriveRuleId` in
 * @centraoneplus/shared (`sla-escalation.types.ts`). The sweep half-keys
 * `@@unique([ticketId, ruleId])` on this exact string, so the two MUST agree
 * byte-for-byte: a level backfilled with a value the sweep would not compute
 * would look brand-new, re-arm for every open ticket and re-page everyone —
 * the very failure this closes (#15034 / #13297). The web bundle cannot import
 * the shared package (it is a Node/Prisma backend package), so the format is
 * reproduced here and pinned by SlaEscalationRulesEditor.rearm.test.tsx.
 *
 * Only the current ("shape D") rule form is handled — the only shape this
 * editor reads or writes. The normalizer's one non-identity transform that
 * touches the id is reproduced: a `before_target` level with no lead of its own
 * inherits the policy's approaching-breach threshold (floored at 1).
 */
export function deriveStableRuleId(rule: EscalationRule, ctx: RuleIdContext = {}): string {
  const actionSig =
    (rule.actions ?? [])
      .map((a) => a.type)
      .sort()
      .join('+') || 'none';

  let when: string;
  if (rule.trigger === 'percent_of_target') {
    when = `pct${rule.thresholdPercent ?? 0}`;
  } else {
    let delay = Math.max(0, rule.delayMinutes ?? 0);
    if (rule.trigger === 'before_target' && delay <= 0) {
      const lead =
        rule.targetType === 'first_response'
          ? ctx.firstResponseWarning ?? undefined
          : ctx.resolutionWarning ?? undefined;
      delay = Math.max(1, Math.max(0, lead ?? DEFAULT_WARNING_LEAD_MINUTES));
    }
    when = `${rule.trigger}${delay}`;
  }
  return `L${rule.level}:${rule.targetType}:${when}:${actionSig}`;
}

const SHAPE_D_TRIGGERS = ['before_target', 'at_breach', 'after_breach', 'percent_of_target'];

/**
 * Fold "config-less" actions (`{ type, userIds, channels, message }`, written
 * straight to the DB by seed scripts) into `{ type, config }` — the only action
 * shape ticket-service's DTO accepts. Without this, saving such a policy round-
 * trips the flat keys and the API rejects it with 400 "property message should
 * not exist".
 *
 * Mirrors the backend normalizer (`asRecord(action.config) ?? action`), so what
 * executes is unchanged: when a `config` object is present the flat keys were
 * already ignored and are dropped; otherwise the flat keys become the config.
 */
export function canonicalizeActions(rules: EscalationRule[]): EscalationRule[] {
  return rules.map((rule) => {
    if (!Array.isArray(rule.actions)) return rule;
    return {
      ...rule,
      actions: rule.actions.map((action) => {
        if (!action || typeof action !== 'object') return action;
        const { type, config, ...flat } = action as EscalationAction & Record<string, unknown>;
        if (Object.keys(flat).length === 0) return action;
        const hasConfig = !!config && typeof config === 'object' && !Array.isArray(config);
        return { type, config: hasConfig ? config : flat } as EscalationAction;
      }),
    };
  });
}

/**
 * Backfill a stable id onto every level that lacks one, computed from the
 * level's CURRENT shape so its identity is PRESERVED — the derived id equals
 * what the sweep has been keying on, so tickets that already escalated are not
 * re-escalated. Run when the form loads a policy: once an id is stored, later
 * threshold edits spread it forward unchanged, so the level stays stable.
 *
 * A level that already carries an id, or that is not the current shape (so its
 * id cannot be reproduced safely), is returned untouched.
 */
export function backfillRuleIds(rules: EscalationRule[], ctx: RuleIdContext = {}): EscalationRule[] {
  return rules.map((rule) =>
    rule.id || !rule.trigger || !SHAPE_D_TRIGGERS.includes(rule.trigger)
      ? rule
      : { ...rule, id: deriveStableRuleId(rule, ctx) },
  );
}

/**
 * Where a level sits relative to the deadline: 0 = before it, 1 = at it,
 * 2 = after it. Used only to flag a ladder authored out of order — the backend
 * fires levels in authored order, so a pre-breach level placed above an
 * at-breach one is held behind it and no longer warns early.
 */
function firePhase(rule: EscalationRule): 0 | 1 | 2 {
  switch (triggerOf(rule).value) {
    case 'before_target':
      return (rule.delayMinutes ?? 0) > 0 ? 0 : 1;
    case 'percent_of_target':
      return (rule.thresholdPercent ?? 0) < 100 ? 0 : 1;
    case 'at_breach':
      return 1;
    default:
      return 2;
  }
}

export default function SlaEscalationRulesEditor({ value, onChange }: Props) {
  const rules = value ?? [];

  const update = (index: number, next: EscalationRule) =>
    onChange(rules.map((r, i) => (i === index ? next : r)));

  const remove = (index: number) =>
    // Renumber so levels stay 1..n contiguous, which is the order they fire in.
    onChange(rules.filter((_, i) => i !== index).map((r, i) => ({ ...r, level: i + 1 })));

  const add = () => onChange([...rules, newRule(rules.length + 1)]);

  // Levels fire in authored order within a target type, so a level that sits
  // EARLIER on the clock than one below it never gets to run early — it waits
  // for its turn and then fires after the deadline it was meant to pre-empt.
  const outOfOrder = new Set<number>();
  const highestPhaseSoFar = new Map<string, number>();
  rules.forEach((rule, index) => {
    const key = rule.targetType ?? 'resolution';
    const phase = firePhase(rule);
    const previous = highestPhaseSoFar.get(key);
    if (previous !== undefined && phase < previous) outOfOrder.add(index);
    highestPhaseSoFar.set(key, Math.max(previous ?? phase, phase));
  });

  const toggleTarget = (index: number, target: EscalationOrgTarget) => {
    const rule = rules[index];
    const current = selectedTargets(rule);
    const next = current.includes(target)
      ? current.filter((t) => t !== target)
      : [...current, target];
    update(index, patchNotifyConfig(rule, { targets: next }));
  };

  const toggleRole = (index: number, role: string) => {
    const rule = rules[index];
    const current = selectedRoles(rule);
    const next = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    // Drop the key entirely when empty rather than persisting `roles: []`,
    // which the backend would otherwise treat as an authored-but-empty list.
    update(index, patchNotifyConfig(rule, { roles: next.length ? next : undefined }));
  };

  const toggleChannel = (index: number, channel: 'in_app' | 'email') => {
    const rule = rules[index];
    const current = selectedChannels(rule);
    const next = current.includes(channel)
      ? current.filter((c) => c !== channel)
      : [...current, channel];
    // Never persist an empty channel list — that would deliver nothing at all.
    update(index, patchNotifyConfig(rule, { channels: next.length ? next : [channel] }));
  };

  return (
    <div className="mt-3 space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Levels fire in order — a level only runs once every level below it has finished. Escalation
        stops automatically as soon as the SLA target is met. Put pre-breach levels first so a
        ticket is escalated while there is still time to save the SLA.
      </p>

      {rules.length === 0 && (
        <div className="p-4 text-sm text-gray-500 bg-gray-50 rounded-lg dark:bg-gray-900 dark:text-gray-400">
          No escalation levels. Without them, a breached ticket only notifies its assignee.
        </div>
      )}

      {rules.map((rule, index) => {
        const trigger = triggerOf(rule);
        const targets = selectedTargets(rule);
        const roles = selectedRoles(rule);
        const channels = selectedChannels(rule);
        const hasOtherActions = (rule.actions ?? []).some(
          (a) => a.type !== 'notify' && a.type !== 'email',
        );

        return (
          <div
            key={index}
            className="p-3 bg-gray-50 rounded-lg space-y-3 dark:bg-gray-900"
            data-testid={`escalation-rule-${index}`}
          >
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Level {rule.level ?? index + 1}
              </span>
              <input
                type="text"
                value={rule.name ?? ''}
                onChange={(e) => update(index, { ...rule, name: e.target.value })}
                placeholder="Level name"
                aria-label={`Level ${index + 1} name`}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
              />
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove level ${index + 1}`}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Watches</label>
                <ThemedSelect
                  value={rule.targetType ?? 'resolution'}
                  onChange={(e) =>
                    update(index, { ...rule, targetType: e.target.value as EscalationRule['targetType'] })
                  }
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                >
                  {TARGET_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Fires</label>
                <ThemedSelect
                  value={trigger.value}
                  onChange={(e) =>
                    update(index, { ...rule, trigger: e.target.value as EscalationRule['trigger'] })
                  }
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                >
                  {TRIGGERS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>

              {/* Offset column. Keyed off the RESOLVED trigger, never off the raw
                  field: a rule with no stored trigger would otherwise show one
                  option in the select and be treated as another here. */}
              {trigger.value !== 'at_breach' && (
                <div>
                  <label
                    htmlFor={`esc-offset-${index}`}
                    className="block text-xs text-gray-500 mb-1 dark:text-gray-400"
                  >
                    {trigger.offsetLabel}
                  </label>
                  <div className="flex items-center gap-1">
                    <input
                      id={`esc-offset-${index}`}
                      type="number"
                      min={0}
                      {...(trigger.value === 'percent_of_target' ? { max: 100 } : {})}
                      value={
                        trigger.value === 'percent_of_target'
                          ? rule.thresholdPercent ?? 80
                          : rule.delayMinutes ?? 0
                      }
                      onChange={(e) =>
                        update(
                          index,
                          trigger.value === 'percent_of_target'
                            ? { ...rule, thresholdPercent: Number(e.target.value) }
                            : { ...rule, delayMinutes: Number(e.target.value) },
                        )
                      }
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">{trigger.unit}</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">
                Notifies <span className="text-gray-400 dark:text-gray-500">— relative to this ticket</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {ORG_TARGETS.map((t) => {
                  const active = targets.includes(t.value);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      title={t.help}
                      aria-pressed={active}
                      onClick={() => toggleTarget(index, t.value)}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        active
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">
                Also notify by role <span className="text-gray-400 dark:text-gray-500">— everyone in the tenant</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {ROLE_TARGETS.map((r) => {
                  const active = roles.includes(r.value);
                  return (
                    <button
                      key={r.value}
                      type="button"
                      title={r.help}
                      aria-pressed={active}
                      onClick={() => toggleRole(index, r.value)}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        active
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                      }`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <span className="text-xs text-gray-500 dark:text-gray-400">Via</span>
              {(['in_app', 'email'] as const).map((c) => (
                <label key={c} className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={channels.includes(c)}
                    onChange={() => toggleChannel(index, c)}
                    className="rounded border-gray-300"
                  />
                  {c === 'in_app' ? 'In-app' : 'Email'}
                </label>
              ))}
            </div>

            {trigger.value === 'before_target' && (rule.delayMinutes ?? 0) <= 0 && (
              <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" />
                No lead time — this level will use the policy&apos;s SLA warning threshold instead of
                firing at the deadline.
              </p>
            )}

            {outOfOrder.has(index) && (
              <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" />
                This level fires earlier than a level below it. Levels run in order, so it will not
                warn early — move it above them.
              </p>
            )}

            {targets.length === 0 && roles.length === 0 && (
              <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" />
                No recipients selected — this level will notify nobody.
              </p>
            )}

            {hasOtherActions && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This level also has actions configured over the API (assign / priority / webhook).
                They are kept as-is.
              </p>
            )}
          </div>
        );
      })}

      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="h-4 w-4 mr-1" />
        Add level
      </Button>
    </div>
  );
}
